import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Collapse, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import { Theme, makeStyles } from '@material-ui/core/styles'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import clsx from 'clsx'
import { ProjectSettings } from 'models/projectSettings'
import { SectionParagraph } from 'models/specificationSection'
import { DRAGGABLE_ITEM_BORDER_RADIUS } from 'pages/ProjectsPage/WorkspacePage/components/Common/DraggableItem'
import {
    TableEditor,
    TableElement,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/TableEditor'
import {
    getFilteredParagraphForPreview,
    onEnterKeyDown,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import OfficeMasterNote from 'pages/ProjectsPage/WorkspacePage/components/OfficeMasterNote'
import SpecifierNote from 'pages/ProjectsPage/WorkspacePage/components/SpecifierNote'
import React from 'react'
import { v4 as newGuid } from 'uuid'
import ImportPreviewContent from './ImportPreviewContent'

const useStyles = makeStyles((theme: Theme) => ({
    SCT: { marginTop: theme.spacing(1.5) },
    PRT: { marginTop: theme.spacing(3) },
    ART: { display: 'inline', minHeight: 40, marginBottom: theme.spacing(1) },
    PR1: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        lineHeight: '30px',
        marginTop: theme.spacing(1.5),
    },
    PR2: {
        marginLeft: theme.spacing(9),
        paddingRight: theme.spacing(2),
    },
    collapseContainer: {
        paddingLeft: (props: ImportPreviewParagraphStyleProps) =>
            props.textType === 'Article'
                ? theme.spacing(0.5)
                : theme.spacing(1.25),
        fontSize: pxToRem(14),
        paddingTop: (props: ImportPreviewParagraphStyleProps) =>
            props.textType !== 'Article' ? theme.spacing(1.25) : 0,
        borderTopRightRadius: (props: ImportPreviewParagraphStyleProps) =>
            props.textType === 'Article' ? 0 : DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        paddingBottom: (props: ImportPreviewParagraphStyleProps) =>
            props.textType === 'Article'
                ? theme.spacing(1.25)
                : theme.spacing(0),
    },
    PR3: { marginTop: theme.spacing(0) },
    PR4: { marginTop: theme.spacing(0) },
    PR5: { marginTop: theme.spacing(0) },
    tableContainer: { marginBottom: theme.spacing(2) },
    unselectedOptionItem: {
        padding: theme.spacing(1),
        color: '#F78909',
        background: '#FFF9F2',
        width: '100%',
    },
    optionItem: {
        border: '1px solid #F78909',
        borderRadius: '100px',
        '&:disabled': {
            color: '#40464F',
            border: '1px solid #40464F',
            background: theme.palette.common.white,
        },
    },
    borderRight: {
        borderTopRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
        borderBottomRightRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    previewNotes: { marginTop: theme.spacing(2) },
    borderExpanded: {
        border: '1px solid rgb(218, 218, 218)',
        borderTop: 'none',
        marginBottom: theme.spacing(1),
        paddingBottom: theme.spacing(2),
        marginTop: -theme.spacing(2),
    },
    articleIcon: {
        cursor: 'pointer',
    },
    articleContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        border: '1px solid rgb(218, 218, 218)',
        width: '100%',
        paddingLeft: theme.spacing(1),
        borderRadius: 4,
    },
    articleBottom: {
        borderBottom: 'none',
        paddingTop: theme.spacing(1),
    },
    titleRightSideItem: {
        width: 'calc(100% - 169px)', // hover actions and expand/callapse icon width
    },
    titleLeftSideItem: { width: 28 },
    draggedItemErrorDarkBorder: {
        border: '1px solid #EB5757',
    },

    EOS: { marginTop: theme.spacing(3) },
    titleText: {
        fontWeight: 400,
        color: '#A78966',
        fontSize: pxToRem(18),
        width: '100%',
        display: 'flex',
        alignItems: 'center',
    },
    ARTspace: {
        width: theme.spacing(4.75),
        display: 'inline-block',
        marginRight: theme.spacing(1),
    },
}))
export interface ImportPreviewParagraphStyleProps {
    textType?: string
}
export interface ImportPreviewParagraphsProps
    extends ImportPreviewParagraphStyleProps {
    data?: Partial<SectionParagraph>[]
    parentCount: number
    settings?: Partial<ProjectSettings> | undefined
    selectedTab?: number
    handleExpandClick?: (selectedArticle?: Partial<SectionParagraph>) => void
    handleCollapseClick?: (selectedArticle?: Partial<SectionParagraph>) => void
    expandedArticles?: string[]
    selectedFamilyId?: string
    firstArticleIndex?: number
    lastFocusedExpandCollapseIconRef?: React.MutableRefObject<number | null>
}

const ImportPreviewParagraphs: React.FC<ImportPreviewParagraphsProps> = ({
    data,
    parentCount,
    settings,
    selectedTab = 0,
    handleExpandClick,
    handleCollapseClick,
    expandedArticles,
    textType,
    selectedFamilyId,
    firstArticleIndex,
    lastFocusedExpandCollapseIconRef,
}) => {
    let paragraph = [<div />]
    let thisCount = 0
    const classes = useStyles({ textType: textType })
    if (data) {
        let isArticleLevel = true
        data?.map((each: Partial<SectionParagraph>, index) => {
            switch (true) {
                case each?.type === 'Table':
                    paragraph.push(
                        <div
                            className={clsx(classes.tableContainer, {
                                [classes.PR1]: each.hierarchicalLevel === 3,
                                [classes.PR2]: each.hierarchicalLevel! > 3,
                            })}
                        >
                            <TableEditor
                                element={
                                    (each.content as unknown) as TableElement
                                }
                                hierarchicalLevel={each.hierarchicalLevel}
                                parentCount={thisCount}
                                projectSettings={settings}
                            />
                        </div>
                    )
                    isArticleLevel = false

                    break
                case each?.type === 'Part':
                    thisCount += 1

                    if (each.children && each.children?.length > 0) {
                        paragraph.push(
                            <ImportPreviewParagraphs
                                data={
                                    firstArticleIndex &&
                                    firstArticleIndex > -1 &&
                                    selectedFamilyId?.substring(0, 2) === '00'
                                        ? each.children?.filter(
                                              (item, index) =>
                                                  item &&
                                                  index >= firstArticleIndex
                                          )
                                        : each?.children
                                }
                                parentCount={thisCount}
                                settings={settings}
                                selectedTab={selectedTab}
                                handleExpandClick={handleExpandClick}
                                handleCollapseClick={handleCollapseClick}
                                expandedArticles={expandedArticles}
                                textType={each.type}
                                selectedFamilyId={selectedFamilyId}
                                firstArticleIndex={firstArticleIndex}
                                lastFocusedExpandCollapseIconRef={
                                    lastFocusedExpandCollapseIconRef
                                }
                            />
                        )
                    }
                    break
                case each?.type === 'Article':
                    const isArticleExpanded = expandedArticles?.includes(
                        each?.content?.[0]?.text!
                    )
                    thisCount += 1
                    paragraph.push(
                        <div
                            className={clsx(
                                classes.articleContainer,
                                classes.ART,
                                isArticleExpanded && classes.articleBottom
                            )}
                        >
                            <GridContainer
                                item
                                alignContent="space-around"
                                alignItems="center"
                                className={classes.titleLeftSideItem}
                            >
                                {isArticleExpanded ? (
                                    <ExpandMoreIcon
                                        data-testid={`collapse${index}`}
                                        ref={(iconElement) => {
                                            // focus on the expand/collapse icon if it was the last focused element
                                            if (
                                                iconElement &&
                                                lastFocusedExpandCollapseIconRef?.current ==
                                                    each.index
                                            ) {
                                                iconElement.focus()
                                            }
                                        }}
                                        htmlColor="#A78966"
                                        className={classes.articleIcon}
                                        onClick={() =>
                                            handleCollapseClick?.(each)
                                        }
                                        onKeyDown={onEnterKeyDown(() => {
                                            handleCollapseClick?.(each)
                                            // set this article's index as last focused expand/collapse icon
                                            if (
                                                lastFocusedExpandCollapseIconRef
                                            ) {
                                                lastFocusedExpandCollapseIconRef.current =
                                                    each.index ?? null
                                            }
                                        })}
                                        tabIndex={0}
                                    />
                                ) : (
                                    <ChevronRightIcon
                                        data-testid={`expand${index}`}
                                        ref={(iconElement) => {
                                            // focus on the expand/collapse icon if it was the last focused element
                                            if (
                                                iconElement &&
                                                lastFocusedExpandCollapseIconRef?.current ==
                                                    each.index
                                            ) {
                                                iconElement.focus()
                                            }
                                        }}
                                        htmlColor="#A78966"
                                        className={classes.articleIcon}
                                        onClick={() =>
                                            handleExpandClick?.(each)
                                        }
                                        onKeyDown={onEnterKeyDown(() => {
                                            handleExpandClick?.(each)
                                            // set this article's index as last focused expand/collapse icon
                                            if (
                                                lastFocusedExpandCollapseIconRef
                                            ) {
                                                lastFocusedExpandCollapseIconRef.current =
                                                    each.index ?? null
                                            }
                                        })}
                                        tabIndex={0}
                                    />
                                )}
                            </GridContainer>

                            <Typography
                                component="span"
                                variant="h6"
                                className={classes.titleText}
                                data-testid={`spanTitle${each.index}`}
                            >
                                <span className={classes.ARTspace}>
                                    {selectedTab + 1}
                                    {'.'}
                                    {thisCount}
                                </span>
                                {each.content?.[0]?.text}
                            </Typography>
                        </div>
                    )
                    paragraph.push(
                        <Collapse
                            in={isArticleExpanded}
                            classes={{
                                root: clsx(
                                    classes.borderRight,
                                    isArticleExpanded && classes.borderExpanded
                                ),
                                wrapper: classes.borderRight,
                                wrapperInner: classes.borderRight,
                            }}
                        >
                            <span className={classes.collapseContainer}>
                                <ImportPreviewParagraphs
                                    data={each.children}
                                    parentCount={thisCount}
                                    settings={settings}
                                    selectedTab={selectedTab}
                                    handleExpandClick={handleExpandClick}
                                    handleCollapseClick={handleCollapseClick}
                                    expandedArticles={expandedArticles}
                                    textType={each.type}
                                    selectedFamilyId={selectedFamilyId}
                                    firstArticleIndex={firstArticleIndex}
                                />
                            </span>
                        </Collapse>
                    )

                    break
                case each?.type === 'Comment':
                    if (!each?.isHidden) {
                        paragraph.push(
                            <GridItem
                                className={clsx('notes', classes.previewNotes)}
                            >
                                <SpecifierNote
                                    key={`previewNote${each.index}`}
                                    notes={[each?.content?.[0]?.text!]}
                                    isHidden={false}
                                    calledFromImport
                                    isArticleLevelComment={isArticleLevel}
                                />
                            </GridItem>
                        )
                    }
                    break
                case each?.type === 'OfficeMasterNote':
                    paragraph.push(
                        <GridItem
                            className={clsx('notes', classes.previewNotes)}
                        >
                            <OfficeMasterNote
                                key={`officeMasterNote${each?.index}`}
                                textContent={each?.content?.[0]?.text || ''}
                                isHidden={false}
                            />
                        </GridItem>
                    )
                    break
                case each?.type === 'OfficeMasterNote':
                    paragraph.push(
                        <GridItem
                            className={clsx('notes', classes.previewNotes)}
                        >
                            <OfficeMasterNote
                                key={`officeMasterNote${each?.index}`}
                                textContent={each?.content?.[0]?.text || ''}
                                isHidden={false}
                            />
                        </GridItem>
                    )
                    break
                case each?.type === 'ReplacementOption':
                    paragraph.push(
                        <Button
                            className={clsx(
                                'optionItem',
                                classes.optionItem,
                                classes.unselectedOptionItem
                            )}
                        >
                            {each?.content?.[0]?.text}
                        </Button>
                    )
                    break

                case each?.type === 'Paragraph':
                    thisCount += 1
                    if (
                        each.content &&
                        each.content?.length > 0 &&
                        !each?.isHidden
                    ) {
                        paragraph.push(
                            <div
                                className={
                                    each.hierarchicalLevel === 2
                                        ? classes.PR1
                                        : classes.PR2
                                }
                            >
                                <ImportPreviewContent
                                    data={each.content}
                                    parentCount={thisCount}
                                    hierarchicalLevel={each.hierarchicalLevel}
                                    settings={settings}
                                />
                            </div>
                        )
                    }
                    //PR2+
                    if (
                        each?.children &&
                        each?.children?.length > 0 &&
                        !each?.isHidden
                    ) {
                        paragraph.push(
                            <ImportPreviewParagraphs
                                data={getFilteredParagraphForPreview(
                                    each.children!
                                )}
                                parentCount={thisCount}
                                settings={settings}
                                selectedTab={selectedTab}
                                handleExpandClick={handleExpandClick}
                                handleCollapseClick={handleCollapseClick}
                                expandedArticles={expandedArticles}
                                textType={each.type}
                                selectedFamilyId={selectedFamilyId}
                                firstArticleIndex={firstArticleIndex}
                            />
                        )
                    }
                    isArticleLevel = false

                    break
            }
        })
    }
    return (
        <>
            {paragraph.map((element) => (
                <div data-testid="importPreviewParagraph" key={newGuid()}>
                    {element}
                </div>
            ))}
        </>
    )
}

export default ImportPreviewParagraphs
